import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { X, Plus, Loader2, AlertTriangle } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { toast } from '@/stores/useToastStore'
import { WaveBreakTable } from './WaveBreakTable'
import {
  useWaveBreaks,
  useCreateWaveBreaks,
  useUpdateWaveBreaks,
  useDeleteWaveBreaks,
} from '@/hooks/useWaveBreaks'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import {
  useDistributionCenters,
  useLineDistributionCenters,
} from '@/hooks/useDistributionCenters'
import {
  mapApiToFrontend,
  mapFrontendToCreateRequest,
  mapFrontendToUpdateRequest,
} from './types'
import type { PlanDetailItem } from '@/types'
import type { DistributionCenter, WaveRow } from './types'

interface PlanWavesDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: PlanDetailItem | null
  planId: string
  versionId: string
}

const ALL_DCS_SENTINEL = '__all_dcs__'

function createDefaultRow(waveNumber: number): WaveRow {
  return {
    waveNumber,
    quantity: null,
    percent: null,
    shipDate: null,
    receiveDate: null,
  }
}

function createDefaultDC(): DistributionCenter {
  return {
    id: 'all-dcs',
    name: 'All Distribution Centers',
    isAllDCs: true,
    selectedDC: '',
    rows: [createDefaultRow(1), createDefaultRow(2)],
  }
}

function createSingleDCEntry(): DistributionCenter {
  return {
    id: 'dc-single',
    name: '',
    isAllDCs: false,
    selectedDC: '',
    rows: [createDefaultRow(1), createDefaultRow(2)],
  }
}

export function PlanWavesDrawer({
  open,
  onOpenChange,
  item,
  planId,
  versionId,
}: PlanWavesDrawerProps) {
  const hasStoreModel = !!item?.storeModelId
  const [distributionType, setDistributionType] = useState<
    'percentage' | 'absolute'
  >('percentage')
  const [distributionCenters, setDistributionCenters] = useState<
    DistributionCenter[]
  >([createDefaultDC()])
  const [isExistingData, setIsExistingData] = useState(false)
  const [applyToAll, setApplyToAll] = useState(false)
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const dcCounterRef = useRef(0)
  const hasHydratedRef = useRef(false)

  // Fetch distribution centers — line-specific when store model exists, global otherwise
  const { data: dcData, isLoading: isLoadingGlobalDCs } =
    useDistributionCenters()
  const { data: lineDCData, isLoading: isLoadingLineDCs } =
    useLineDistributionCenters(
      open ? planId : undefined,
      open ? versionId : undefined,
      open && hasStoreModel ? item?.id : undefined
    )
  const isLoadingDCs = hasStoreModel ? isLoadingLineDCs : isLoadingGlobalDCs
  const dcOptions = useMemo(() => {
    if (hasStoreModel && lineDCData) {
      return lineDCData.map(dc => ({ id: dc.id, name: dc.name }))
    }
    return dcData?.data.map(dc => ({ id: dc.id, name: dc.name })) ?? []
  }, [hasStoreModel, lineDCData, dcData])

  // Fetch wave breaks when drawer opens
  const {
    data: waveBreaksData,
    isLoading,
    isFetched,
  } = useWaveBreaks(
    open ? planId : undefined,
    open ? versionId : undefined,
    open ? item?.id : undefined
  )

  const createMutation = useCreateWaveBreaks(planId, versionId)
  const updateMutation = useUpdateWaveBreaks(planId, versionId)
  const deleteMutation = useDeleteWaveBreaks(planId, versionId)

  const isSaving = createMutation.isPending || updateMutation.isPending

  // Populate local state from fetched data (only once per drawer open)
  useEffect(() => {
    if (!isFetched || !open || hasHydratedRef.current) return
    hasHydratedRef.current = true

    if (
      waveBreaksData &&
      waveBreaksData.items &&
      waveBreaksData.items.length > 0
    ) {
      const { distributionType: dt, distributionCenters: dcs } =
        mapApiToFrontend(waveBreaksData)
      setDistributionType(dt)
      setDistributionCenters(dcs)
      setIsExistingData(true)
    } else {
      setDistributionType('percentage')
      setDistributionCenters([
        hasStoreModel ? createDefaultDC() : createSingleDCEntry(),
      ])
      setIsExistingData(false)
    }
  }, [waveBreaksData, isFetched, open, hasStoreModel])

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setDistributionType('percentage')
      setDistributionCenters([
        hasStoreModel ? createDefaultDC() : createSingleDCEntry(),
      ])
      setIsExistingData(false)
      setApplyToAll(false)
      setConfirmRemoveOpen(false)
      setShowErrors(false)
      hasHydratedRef.current = false
    }
  }, [open, hasStoreModel])

  const handleAddRow = useCallback((dcId: string) => {
    setDistributionCenters(prev =>
      prev.map(dc => {
        if (dc.id !== dcId) return dc
        const nextNumber =
          dc.rows.length > 0
            ? Math.max(...dc.rows.map(r => r.waveNumber)) + 1
            : 1
        return { ...dc, rows: [...dc.rows, createDefaultRow(nextNumber)] }
      })
    )
  }, [])

  const handleDeleteRow = useCallback((dcId: string, rowIndex: number) => {
    setDistributionCenters(prev =>
      prev.map(dc => {
        if (dc.id !== dcId) return dc
        const newRows = dc.rows.filter((_, i) => i !== rowIndex)
        return { ...dc, rows: newRows }
      })
    )
  }, [])

  const handleUpdateRow = useCallback(
    (dcId: string, rowIndex: number, field: keyof WaveRow, value: unknown) => {
      setDistributionCenters(prev =>
        prev.map(dc => {
          if (dc.id !== dcId) return dc
          const newRows = dc.rows.map((row, i) => {
            if (i !== rowIndex) return row
            return { ...row, [field]: value }
          })
          return { ...dc, rows: newRows }
        })
      )
    },
    []
  )

  const handleAddDC = useCallback(() => {
    dcCounterRef.current += 1
    const newDC: DistributionCenter = {
      id: `dc-${dcCounterRef.current}`,
      name: '',
      isAllDCs: false,
      selectedDC: '',
      rows: [createDefaultRow(1), createDefaultRow(2)],
    }
    setDistributionCenters(prev => [...prev, newDC])
  }, [])

  const handleRemoveDC = useCallback((dcId: string) => {
    setDistributionCenters(prev => prev.filter(dc => dc.id !== dcId))
  }, [])

  const handleDCChange = useCallback(
    (dcId: string, value: string) => {
      if (value === ALL_DCS_SENTINEL) {
        setDistributionCenters(prev =>
          prev.map(dc => {
            if (dc.id !== dcId) return dc
            return {
              ...dc,
              isAllDCs: true,
              selectedDC: '',
              name: 'All Distribution Centers',
            }
          })
        )
      } else {
        const dcName = dcOptions.find(opt => opt.id === value)?.name ?? value
        setDistributionCenters(prev =>
          prev.map(dc => {
            if (dc.id !== dcId) return dc
            return { ...dc, isAllDCs: false, selectedDC: value, name: dcName }
          })
        )
      }
    },
    [dcOptions]
  )

  const handleConfirmRemove = useCallback(() => {
    if (!item) return
    deleteMutation.mutate(
      { lineId: item.id },
      {
        onSuccess: () => {
          setConfirmRemoveOpen(false)
          onOpenChange(false)
        },
      }
    )
  }, [item, deleteMutation, onOpenChange])

  const handleSave = () => {
    if (!item) return

    const hasInvalidRows = distributionCenters.some(dc =>
      dc.rows.some(row =>
        distributionType === 'percentage'
          ? row.percent === null || row.percent === undefined
          : row.quantity === null || row.quantity === undefined
      )
    )

    if (hasInvalidRows) {
      setShowErrors(true)
      toast.error('Please fill in all required fields')
      return
    }

    if (isExistingData) {
      // Existing wave breaks → PUT
      const body = mapFrontendToUpdateRequest(
        distributionType,
        distributionCenters,
        applyToAll
      )
      updateMutation.mutate(
        { lineId: item.id, body },
        { onSuccess: () => onOpenChange(false) }
      )
    } else {
      // New wave breaks → POST
      const body = mapFrontendToCreateRequest(
        distributionType,
        distributionCenters,
        applyToAll
      )
      createMutation.mutate(
        { lineId: item.id, body },
        { onSuccess: () => onOpenChange(false) }
      )
    }
  }

  if (!item) return null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex w-200 flex-col sm:max-w-200"
      >
        {/* Header */}
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle className="text-lg font-semibold">
                  {item.description}
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Wave break model configuration
                </SheetDescription>
                <Badge variant="neutral">Wave Break Model</Badge>
              </div>
              <span className="text-sm text-muted-foreground">
                SKU #{item.skuNumber}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <SheetClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 cursor-pointer"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </SheetClose>
            </div>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Distribution Type Toggle */}
              <div>
                <span className="text-sm font-medium text-[#1c1c1c] mb-3 block">
                  Distribution Type
                </span>
                <RadioGroup
                  value={distributionType}
                  onValueChange={val =>
                    setDistributionType(val as 'percentage' | 'absolute')
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem
                      value="percentage"
                      id="dist-percentage"
                      className="cursor-pointer"
                    />
                    <Label
                      htmlFor="dist-percentage"
                      className="text-sm text-[#1c1c1c] cursor-pointer"
                    >
                      Percentage
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem
                      value="absolute"
                      id="dist-absolute"
                      className="cursor-pointer"
                    />
                    <Label
                      htmlFor="dist-absolute"
                      className="text-sm text-[#1c1c1c] cursor-pointer"
                    >
                      Absolute Quantity
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Distribution Center Tables */}
              {distributionCenters.map(dc => {
                const usedDCs = distributionCenters
                  .filter(other => other.id !== dc.id)
                  .flatMap(other => {
                    const ids: string[] = []
                    if (other.isAllDCs) ids.push(ALL_DCS_SENTINEL)
                    if (other.selectedDC) ids.push(other.selectedDC)
                    return ids
                  })
                return (
                  <WaveBreakTable
                    key={dc.id}
                    isAllDCs={dc.isAllDCs}
                    distributionType={distributionType}
                    rows={dc.rows}
                    onAddRow={() => handleAddRow(dc.id)}
                    onDeleteRow={index => handleDeleteRow(dc.id, index)}
                    onUpdateRow={(index, field, value) =>
                      handleUpdateRow(dc.id, index, field, value)
                    }
                    onRemove={
                      !hasStoreModel ? undefined : () => handleRemoveDC(dc.id)
                    }
                    selectedDC={dc.isAllDCs ? ALL_DCS_SENTINEL : dc.selectedDC}
                    onDCChange={value => handleDCChange(dc.id, value)}
                    usedDCs={usedDCs}
                    dcOptions={dcOptions}
                    isLoadingDCs={isLoadingDCs}
                    showErrors={showErrors}
                    allDcsSentinel={ALL_DCS_SENTINEL}
                    hasStoreModel={hasStoreModel}
                  />
                )
              })}

              {/* Add Distribution Center */}
              {hasStoreModel &&
                distributionCenters.filter(dc => !dc.isAllDCs).length <
                  dcOptions.length && (
                  <button
                    onClick={handleAddDC}
                    className="flex items-center gap-1.5 text-sm font-medium text-[#CF1F2E] hover:text-[#CF1F2E]/80 cursor-pointer"
                  >
                    <Plus className="h-4 w-4" />
                    Add Distribution Center
                  </button>
                )}

              {/* Apply to All Lines */}
              <div className="border-t border-[#e7e7e7] pt-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="apply-to-all"
                    checked={applyToAll}
                    onCheckedChange={checked => setApplyToAll(checked === true)}
                    className="cursor-pointer mt-0.5"
                  />
                  <div>
                    <label
                      htmlFor="apply-to-all"
                      className="text-sm font-medium cursor-pointer"
                    >
                      Apply to all other lines in this plan
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Use this wave break model as a template for every line
                      item.
                    </p>
                  </div>
                </div>
                {applyToAll && (
                  <Alert variant="warning" className="mt-3">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      This will overwrite existing wave breaks on all other line
                      items. This action cannot be undone.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <SheetFooter className="border-t px-6 py-4">
          <div className="flex items-center justify-between w-full">
            <div>
              {isExistingData && (
                <button
                  type="button"
                  className="text-sm text-destructive hover:text-destructive/80 underline cursor-pointer"
                  onClick={() => setConfirmRemoveOpen(true)}
                >
                  Remove Wave Break Model
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || isLoading}
                className="bg-[#CF1F2E] hover:bg-[#CF1F2E]/90 text-white"
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Wave Break Model
              </Button>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>

      <ConfirmationDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title="Remove Wave Break Model"
        description="Are you sure you want to remove the wave break model? This action cannot be undone."
        variant="destructive"
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
        onConfirm={handleConfirmRemove}
      />
    </Sheet>
  )
}
